/**
 * features/finance/FeesSpecialCases.tsx
 * ------------------------------------------------------------------
 * Fee exceptions. Only staff children are left here, and they are waiting on a
 * school decision.
 *
 * BURSARIES AND FULL ANNUAL PAYMENT WERE REMOVED (the school, Nov 2026): every
 * discount now goes through the Discounts screen, issued against one invoice.
 * The billing run no longer reads either. Their records stay in Firestore: no
 * award was ever made, and one full-annual request from 9 Sep 2026 was never
 * decided. The screens are in git history before this change.
 *
 * STAFF CHILDREN IS DELIBERATELY NOT BUILT YET, and says so rather than
 * being quietly missing. The figure that screen would compute goes onto a
 * payslip, and computing it needs the school's remission rule — how many
 * children, at what percentage. That rule has not been confirmed, and the
 * sister system's version of it (first two free, next two at half) is that
 * school's policy, not this one's. A tab that guesses would be worse than a
 * tab that waits: HR can already record the deduction by hand, which is what
 * happens today.
 * ------------------------------------------------------------------ */
import { HeartHandshake, Info } from 'lucide-react';
import { PageHeader, SoftCard } from '@/features/finance/ui';

export default function FeesSpecialCases() {
  return (
    <div>
      <PageHeader
        icon={HeartHandshake}
        title="Fee Special Cases"
        subtitle="Staff children. Discounts are issued from the Discounts screen."
      />
      <StaffChildrenPending />
    </div>
  );
}

/**
 * Stated plainly rather than left as an empty panel. Someone opening this tab
 * is looking for a thing they were told exists; the useful answer is what is
 * missing, why, and where the work happens meanwhile.
 */
function StaffChildrenPending() {
  return (
    <SoftCard>
      <div className="flex items-start gap-3">
        <Info size={20} className="text-primary dark:text-primarySoft mt-0.5 shrink-0" />
        <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
          <p className="font-medium text-gray-800 dark:text-gray-100">
            Not built yet — waiting on the school&rsquo;s staff fee remission rule.
          </p>
          <p>
            This screen will list each staff member&rsquo;s enrolled children, work out what they
            owe from the real fee structures, and push the net to payroll as a school-fees
            deduction. Pupils covered are then left out of the billing run, because the benefit is
            settled through payroll rather than by raising an invoice and discounting it away.
          </p>
          <p>
            What it needs first is the rule itself: how many children are covered and at what
            percentage. That figure lands on a payslip, so it is not something to infer.
          </p>
          <p className="text-gray-500 dark:text-gray-400">
            Until then, HR records the deduction by hand under
            {' '}<span className="font-medium">Payroll &rsaquo; Deductions</span>{' '}
            as &ldquo;School Fees &amp; Exams&rdquo;, exactly as it works today. Nothing has been
            taken away.
          </p>
        </div>
      </div>
    </SoftCard>
  );
}
